import {
	faAngleDown,
	faAngleUp,
	faFloppyDisk,
	faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { generateUniqueId } from "../../../../../common/generateUniqueId";
import { env } from "../../../../../env/env";
import {
	clearDeviceSlot,
	createDeviceSlot,
	deleteDeviceSlot,
	deleteMemory,
	insertMemory,
	selectDevices,
	selectMemories,
	updateDeviceSlot,
} from "../../../../editor/editorSlice";
import {
	Device,
	Input,
	Output,
	createDevice,
} from "../../../../editor/interfaces/components/Device";
import Memory from "../../../../editor/interfaces/components/Memory";
import { VariableContent } from "../../../../editor/interfaces/components/Variable";
import { selectLogics } from "../../../../logics/logicsSlice";
import { WindowTypeAndPropsDefinition } from "../../../windowManagerSlice";
import {
	BooleanVariable,
	EnumVariable,
	NumberVariable,
} from "../BlockConfigWindow/BlockConfigWindow";
import css from "./DeviceManagerWindow.module.css";

export interface DeviceManagerWindowProps {
	initialCategory?: string;
}

export type DeviceManagerWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"deviceManager",
	DeviceManagerWindowProps
>;

export function DeviceManagerWindow(
	props: DeviceManagerWindowProps & { id: string },
) {
	const dispatch = useAppDispatch();

	const logics = useAppSelector(selectLogics);
	const devices = useAppSelector(selectDevices);
	const memories = useAppSelector(selectMemories);
	const [selectedDeviceIndex, setSelectedDeviceIndex] = useState<number | null>(
		null,
	);
	const [deviceScratchPad, setDeviceScratchPad] = useState<Device | null>(null);
	const [unableToSaveMessage, setUnableToSaveMessage] = useState<string | null>(
		null,
	);

	const deviceConfig = deviceScratchPad
		? logics.device[deviceScratchPad.name]
		: undefined;

	useEffect(() => {
		return () => {
			// On unmount clear null devices
			dispatch(clearDeviceSlot());
		};
	}, []);

	const setVariableContent =
		(configType: string, configIndex: number, variableName: string) =>
		(content: VariableContent) => {
			if (deviceScratchPad !== null) {
				const device = {
					...deviceScratchPad,
					configs: {
						...deviceScratchPad.configs,
						[configType]: deviceScratchPad.configs[configType].map(
							(config, index) => {
								if (index !== configIndex) {
									return config;
								}

								return {
									...config,
									variables: {
										...config.variables,
										[variableName]: {
											...config.variables[variableName],
											content,
										},
									},
								};
							},
						),
					},
				};

				setDeviceScratchPad(device);
			}
		};

	const setInput = (inputIndex: number, newInput: Input) => {
		if (deviceScratchPad !== null) {
			const device = {
				...deviceScratchPad,
				inputs: deviceScratchPad.inputs.map((input, index) => {
					if (index !== inputIndex) {
						return input;
					}

					return newInput;
				}),
			};

			setDeviceScratchPad(device);
		}
	};

	const setOutput = (outputIndex: number, newOutput: Output) => {
		if (deviceScratchPad !== null) {
			const device = {
				...deviceScratchPad,
				outputs: deviceScratchPad.outputs.map((output, index) => {
					if (index !== outputIndex) {
						return output;
					}

					return newOutput;
				}),
			};

			setDeviceScratchPad(device);
		}
	};

	const [metadataOpen, setMetadataOpen] = useState<boolean>(true);
	const [inputsOpen, setInputsOpen] = useState<boolean>(false);
	const [outputsOpen, setOutputsOpen] = useState<boolean>(false);
	const [configurationsOpen, setConfigurationsOpen] = useState<boolean>(false);

	const [inputOpen, setInputOpen] = useState<boolean[]>([]);
	const [outputOpen, setOutputOpen] = useState<boolean[]>([]);
	const [configurationOpen, setConfigurationOpen] = useState<boolean[]>([]);
	const [configurationDetailsOpen, setConfigurationDetailsOpen] = useState<
		boolean[][]
	>([]);

	const renderConfigs = (
		config: Input | Output,
		index: number,
		key: string,
		value: Array<string> | number | boolean,
		type: "input" | "output",
	) => {
		switch (true) {
			case value instanceof Array:
				return (
					<select
						className={css.select}
						value={`${config[key]}`}
						onChange={(event) => {
							if (type === "input") {
								setInput(index, {
									...config,
									[key]: event.target.value,
								});
							}

							if (type === "output") {
								setOutput(index, {
									...config,
									[key]: event.target.value,
								});
							}
						}}
					>
						{value.map((mode) => (
							<option key={mode} value={mode}>
								{mode}
							</option>
						))}
					</select>
				);
			case typeof value === "number":
				return (
					<input
						type="number"
						defaultValue={value}
						value={+config[key]}
						onChange={(event) => {
							if (type === "input") {
								setInput(index, {
									...config,
									[key]: event.target.value,
								});
							}

							if (type === "output") {
								setOutput(index, {
									...config,
									[key]: event.target.value,
								});
							}
						}}
					/>
				);
			case typeof value === "boolean":
				return (
					<input
						type="checkbox"
						value={`${config[key]}`}
						checked={Boolean(config[key])}
						onChange={(event) => {
							if (type === "input") {
								setInput(index, {
									...config,
									[key]: !config[key],
								});
							}

							if (type === "output") {
								setOutput(index, {
									...config,
									[key]: !config[key],
								});
							}
						}}
					/>
				);
			default:
				return <></>;
		}
	};

	const importVariablesAndMessages = async (device: Device) => {
		const response = await fetch(`${env.apiUrl}/import-default-messages`);

		if (!response.ok) {
			throw new Error("Failed to import default variables and messages");
		}

		const { messages, variables } = await response.json();

		const variableLogic = logics.memory.VARIABLE;
		const messageLogic = logics.memory.MESSAGE;

		const existingMemoryLabels = new Set(
			Object.values(memories).flatMap((category) =>
				category.map((memory) => memory.label),
			),
		);

		variables.forEach((variable: Memory, index: number) => {
			if (existingMemoryLabels.has(variable.label)) {
				return;
			}

			existingMemoryLabels.add(variable.label);

			const configVariables = variableLogic.variables.reduce((init, config) => {
				return {
					...init,
					[config.name]: {
						label: config.label,
						content: {
							type: config.content.type,
							value: variable.metadata?.variables
								? variable.metadata.variables[config.name]
								: config.content.default,
						},
					},
				};
			}, {});

			dispatch(
				insertMemory({
					memory: {
						...variable,
						id: generateUniqueId("memory"),
						metadata: {
							...variable.metadata,
							variables: configVariables,
						},
						source: variable.source
							? variable.source
							: {
									type: "address",
									deviceId: device.id,
									address: index,
								},
					},
				}),
			);
		});

		messages.forEach((message: Memory, index: number) => {
			if (existingMemoryLabels.has(message.label)) {
				return;
			}

			existingMemoryLabels.add(message.label);

			const configMessages = messageLogic.variables.reduce(
				(init, message) => ({
					...init,
					[message.name]: {
						label: message.label,
						content: {
							type: message.content.type,
							value: message.content.default,
						},
					},
				}),
				{},
			);

			dispatch(
				insertMemory({
					memory: {
						...message,
						id: generateUniqueId("memory"),
						metadata: {
							...message.metadata,
							variables: configMessages,
						},
						source: {
							type: "address",
							deviceId: device.id,
							address: index,
						},
					},
				}),
			);
		});
	};

	const createDefaultMemories = (device: Device, category: string) => {
		const variables = logics.memory[category].variables.reduce(
			(acc, variable) => {
				return {
					...acc,
					[variable.name]: {
						label: variable.label,
						content: {
							type: variable.content.type,
							value: variable.content.default,
						},
					},
				};
			},
			{},
		);

		const data = logics.memory[category].data?.reduce((acc, data) => {
			return {
				...acc,
				[data.size]: "",
			};
		}, {});

		const looping = category === "INPUT" ? "inputs" : "outputs";

		const regexCurrent = /^(?!.*CS).*$/;
		const regexOin = /^(?!.*IN).*$/;

		device[looping]
			.filter((io) => regexCurrent.test(io.name) && regexOin.test(io.name))
			.forEach((io) => {
				dispatch(
					insertMemory({
						memory: {
							id: generateUniqueId("memory"),
							label: io.name,
							name: category,
							source: {
								type: "io",
								deviceId: device.id,
								io: io.name,
							},
							broadcast: false,
							metadata: {
								variables: variables,
								data: data,
							},
						} as Memory,
					}),
				);
			});
	};

	return (
		<div className={css.content}>
			<div className={css.sideBar}>
				<button
					className={css.creatorButton}
					onClick={() => {
						dispatch(createDeviceSlot());
					}}
				>
					Create device
				</button>
				<div className={css.list}>
					{devices.map((device, index) => {
						return (
							<div
								key={`devices-${index}`}
								className={classNames(css.listItem, {
									[css.listItemSelected]: index === selectedDeviceIndex,
									[css.listItemUninitialized]:
										device === null ||
										device.address === undefined ||
										devices
											.map((device) => device?.address)
											.filter(
												(device, otherIndex) =>
													device !== undefined && otherIndex !== index,
											)
											.includes(device?.address),
								})}
								onClick={() => {
									setSelectedDeviceIndex(index);
									setDeviceScratchPad(device);
									if (device) {
										setInputsOpen(false);
										setOutputsOpen(false);
										setConfigurationsOpen(false);

										setInputOpen(device.inputs.map(() => false));
										setOutputOpen(device.outputs.map(() => false));

										const configs = Object.values(device.configs);
										setConfigurationOpen(configs.map(() => false));
										setConfigurationDetailsOpen(
											configs.map((config) => config.map(() => false)),
										);
									}
								}}
							>
								{device?.label ?? "[ Uninitialized ]"}
							</div>
						);
					})}
				</div>
			</div>
			{selectedDeviceIndex !== null && (
				<div key={selectedDeviceIndex} className={css.deviceEditor}>
					<div className={css.row}>
						<button
							id="save-device-button"
							className={css.button}
							onClick={() => {
								if (!deviceScratchPad?.address) {
									setUnableToSaveMessage("Select address to save device");
									return;
								}

								setUnableToSaveMessage(null);

								if (deviceScratchPad !== null) {
									dispatch(
										updateDeviceSlot({
											index: selectedDeviceIndex,
											device: deviceScratchPad,
										}),
									);

									createDefaultMemories(deviceScratchPad, "INPUT");
									createDefaultMemories(deviceScratchPad, "OUTPUT");

									if (deviceScratchPad.name == "ICU820") {
										importVariablesAndMessages(deviceScratchPad);
									}

									const device = devices[selectedDeviceIndex];

									if (device === null) {
										return;
									}

									if (
										deviceScratchPad.name &&
										deviceScratchPad.name !== device.name
									) {
										const associatedMemories = Object.values(memories).flatMap(
											(memories) => {
												return memories
													.map(
														(memory, index) =>
															[memory, index] as [Memory, number],
													)
													.filter(
														([memory]) =>
															memory.source.type !== "local" &&
															memory.source.deviceId === device.id,
													);
											},
										);

										associatedMemories
											.toReversed()
											.forEach(([memory, index]) => {
												dispatch(
													deleteMemory({
														memoryName: memory.name,
														index,
													}),
												);
											});
									}
								}
							}}
						>
							<FontAwesomeIcon icon={faFloppyDisk} />
						</button>
						<select
							id="select-device"
							className={classNames(css.select, css.deviceLabelSelector)}
							value={deviceScratchPad?.label ?? ""}
							onChange={(event) => {
								const device = createDevice(event.target.value, logics);
								console.log({ device });
								const firstAddress = logics.device[device.name]?.addresses[0];

								setDeviceScratchPad({ ...device, address: firstAddress });

								setMetadataOpen(true);
								setInputsOpen(false);
								setOutputsOpen(false);
								setConfigurationsOpen(false);

								setInputOpen(device.inputs.map(() => false));
								setOutputOpen(device.outputs.map(() => false));

								const configs = Object.values(device.configs);
								setConfigurationOpen(configs.map(() => false));
								setConfigurationDetailsOpen(
									configs.map((config) => config.map(() => false)),
								);
							}}
						>
							{deviceScratchPad === null && <option value={""} />}
							{Object.keys(logics.device).map((name) => {
								return (
									<option key={name} value={name}>
										{name}
									</option>
								);
							})}
						</select>
						{selectedDeviceIndex !== null && (
							<button
								className={classNames(css.button, css.deleteButton)}
								onClick={() => {
									setDeviceScratchPad(null);
									setSelectedDeviceIndex(null);
									dispatch(
										deleteDeviceSlot({
											index: selectedDeviceIndex,
										}),
									);

									const device = devices[selectedDeviceIndex];

									if (device === null) {
										return;
									}

									const associatedMemories = Object.values(memories).flatMap(
										(memories) => {
											return memories
												.map(
													(memory, index) =>
														[memory, index] as [Memory, number],
												)
												.filter(
													([memory]) =>
														memory.source.type !== "local" &&
														memory.source.deviceId === device.id,
												);
										},
									);

									associatedMemories.toReversed().forEach(([memory, index]) => {
										dispatch(
											deleteMemory({
												memoryName: memory.name,
												index,
											}),
										);
									});
								}}
							>
								<FontAwesomeIcon icon={faTrash} />
							</button>
						)}
					</div>
					{unableToSaveMessage && (
						<div
							className={css.row}
							style={{
								color: "var(--palette-red)",
							}}
						>
							{unableToSaveMessage}
						</div>
					)}
					{deviceScratchPad !== null && deviceConfig && (
						<>
							<div className={css.row}>
								<div
									className={css.sectionTitle}
									onClick={() => {
										setMetadataOpen(!metadataOpen);
									}}
								>
									Metadata
									{metadataOpen ? (
										<FontAwesomeIcon icon={faAngleUp} />
									) : (
										<FontAwesomeIcon icon={faAngleDown} />
									)}
								</div>
							</div>
							{metadataOpen && (
								<div className={css.innerRow}>
									<div className={css.row}>
										<div className={css.label}>Address</div>
										<select
											id="select-device-address"
											className={css.select}
											value={deviceScratchPad.address}
											onChange={(event) => {
												if (deviceScratchPad !== null) {
													setDeviceScratchPad({
														...deviceScratchPad,
														address: event.target.value,
													});
												}
											}}
										>
											{deviceScratchPad.address === undefined && (
												<option value={""} />
											)}
											{deviceConfig.addresses.map((address) => {
												return (
													<option key={address} value={address}>
														{address}
													</option>
												);
											})}
										</select>
									</div>
								</div>
							)}
							<div className={css.row}>
								<div
									className={css.sectionTitle}
									onClick={() => {
										setInputsOpen(!inputsOpen);
									}}
								>
									Inputs
									{inputsOpen ? (
										<FontAwesomeIcon icon={faAngleUp} />
									) : (
										<FontAwesomeIcon icon={faAngleDown} />
									)}
								</div>
							</div>
							{inputsOpen && (
								<div className={css.innerRow}>
									{deviceScratchPad.inputs.map((input, index) => {
										const { id, ...variables } = deviceConfig.input[input.name];

										return (
											<React.Fragment key={`device-scratch-input-${index}`}>
												<div
													className={css.row}
													onClick={() => {
														setInputOpen((inputs) => {
															const newInputs = [...inputs];
															newInputs[index] = !newInputs[index];
															return newInputs;
														});
													}}
												>
													<div className={css.sectionTitle}>
														{input.name}
														{inputOpen[index] && (
															<FontAwesomeIcon icon={faAngleUp} />
														)}
														{!inputOpen[index] && (
															<FontAwesomeIcon icon={faAngleDown} />
														)}
													</div>
												</div>
												{inputOpen[index] &&
													Object.entries(variables).map(([key, variable]) => {
														return (
															<div
																key={`input-${index}-${key}`}
																className={css.innerRow}
															>
																<div className={css.row}>
																	<div className={css.label}>{key}</div>
																	{renderConfigs(
																		input,
																		index,
																		key,
																		variable,
																		"input",
																	)}
																</div>
															</div>
														);
													})}
											</React.Fragment>
										);
									})}
								</div>
							)}
							<div className={css.row}>
								<div
									className={css.sectionTitle}
									onClick={() => {
										setOutputsOpen(!outputsOpen);
									}}
								>
									Outputs
									{outputsOpen ? (
										<FontAwesomeIcon icon={faAngleUp} />
									) : (
										<FontAwesomeIcon icon={faAngleDown} />
									)}
								</div>
							</div>
							{outputsOpen && (
								<div className={css.innerRow}>
									{deviceScratchPad.outputs.map((output, index) => {
										const { id, ...variables } =
											deviceConfig.output[output.name];

										return (
											<React.Fragment key={`device-scratch-output-${index}`}>
												<div
													className={css.row}
													onClick={() => {
														setOutputOpen((outputs) => {
															const newOutputs = [...outputs];
															newOutputs[index] = !newOutputs[index];
															return newOutputs;
														});
													}}
												>
													<div className={css.sectionTitle}>
														{output.name}
														{outputOpen[index] && (
															<FontAwesomeIcon icon={faAngleUp} />
														)}
														{!outputOpen[index] && (
															<FontAwesomeIcon icon={faAngleDown} />
														)}
													</div>
												</div>
												{outputOpen[index] &&
													Object.entries(variables).map(([key, variable]) => {
														return (
															<div
																key={`output-${index}-${key}`}
																className={css.innerRow}
															>
																<div className={css.row}>
																	<div className={css.label}>{key}</div>
																	{renderConfigs(
																		output,
																		index,
																		key,
																		variable,
																		"output",
																	)}
																</div>
															</div>
														);
													})}
											</React.Fragment>
										);
									})}
								</div>
							)}
							<div className={css.row}>
								<div
									className={css.sectionTitle}
									onClick={() => {
										setConfigurationsOpen(!configurationsOpen);
									}}
								>
									Configurations
									{configurationsOpen ? (
										<FontAwesomeIcon icon={faAngleUp} />
									) : (
										<FontAwesomeIcon icon={faAngleDown} />
									)}
								</div>
							</div>
							{configurationsOpen && (
								<div className={css.innerRow}>
									{Object.entries(deviceConfig.config).map(
										([configType, config], index) => {
											const configConfig = logics.config[configType];
											const deviceConfigs =
												deviceScratchPad.configs[configConfig.type];
											const configVariables =
												logics.config[configConfig.type].variables;

											return (
												<React.Fragment key={`device-config-${index}`}>
													<div
														className={css.row}
														onClick={() => {
															setConfigurationOpen((configuration) => {
																const newConfigurations = [...configuration];

																newConfigurations[index] =
																	!newConfigurations[index];
																return newConfigurations;
															});
														}}
													>
														<div className={css.sectionTitle}>
															{configConfig.label}
															{configurationOpen[index] && (
																<FontAwesomeIcon icon={faAngleUp} />
															)}
															{!configurationOpen[index] && (
																<FontAwesomeIcon icon={faAngleDown} />
															)}
														</div>
													</div>
													{configurationOpen[index] && (
														<div className={css.innerRow}>
															{deviceConfigs.map((config, configIndex) => {
																const variableHtml = configVariables.map(
																	(logicsVariable, variableIndex) => {
																		const variable =
																			config.variables[logicsVariable.label];

																		return (
																			<div
																				key={`config-variable-${variableIndex}`}
																				className={css.row}
																			>
																				<div className={css.label}>
																					{logicsVariable.label}
																				</div>
																				{logicsVariable.private &&
																					variable.content.value}
																				{!logicsVariable.private && (
																					<>
																						{variable.content.type ===
																							"boolean" && (
																							<BooleanVariable
																								content={variable.content}
																								config={logicsVariable}
																								onChange={setVariableContent(
																									configConfig.type,
																									configIndex,
																									logicsVariable.label,
																								)}
																							/>
																						)}
																						{variable.content.type ===
																							"enum" && (
																							<EnumVariable
																								content={variable.content}
																								config={logicsVariable}
																								onChange={setVariableContent(
																									configConfig.type,
																									configIndex,
																									logicsVariable.label,
																								)}
																							/>
																						)}
																						{variable.content.type ===
																							"number" && (
																							<NumberVariable
																								content={variable.content}
																								config={logicsVariable}
																								onChange={setVariableContent(
																									configConfig.type,
																									configIndex,
																									logicsVariable.label,
																								)}
																							/>
																						)}
																					</>
																				)}
																			</div>
																		);
																	},
																);

																return (
																	<React.Fragment key={configIndex}>
																		{
																			<div className={css.innerRow}>
																				<div
																					className={css.row}
																					onClick={(e) => {
																						e.stopPropagation();

																						setConfigurationDetailsOpen(
																							(details) => {
																								const newDetails = [...details];

																								return newDetails.map(
																									(detail, idx) => {
																										if (index === idx) {
																											return detail.map(
																												(d, i) => {
																													return configIndex ===
																														i
																														? !d
																														: d;
																												},
																											);
																										}
																										return detail;
																									},
																								);
																							},
																						);
																					}}
																				>
																					<div className={css.sectionTitle}>
																						{config.details ?? configIndex}
																						{configurationDetailsOpen[index][
																							configIndex
																						] && (
																							<FontAwesomeIcon
																								icon={faAngleUp}
																							/>
																						)}
																						{!configurationDetailsOpen[index][
																							configIndex
																						] && (
																							<FontAwesomeIcon
																								icon={faAngleDown}
																							/>
																						)}
																					</div>
																				</div>
																				{configurationDetailsOpen[index][
																					configIndex
																				] && (
																					<div className={css.innerRow}>
																						{variableHtml}
																					</div>
																				)}
																			</div>
																		}
																	</React.Fragment>
																);
															})}
														</div>
													)}
												</React.Fragment>
											);
										},
									)}
								</div>
							)}
						</>
					)}
					<div className={css.bottomMargin} />
				</div>
			)}
		</div>
	);
}
